import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { faqs } from '../utils/mock';
import PageTitle from '../hooks/PageTitle';

const FAQ = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', ...new Set(faqs.map(f => f.category))];

  const filteredFaqs = selectedCategory === 'All' 
    ? faqs 
    : faqs.filter(faq => faq.category === selectedCategory);

  const toggleFaq = (id) => {
    setOpenFaq(openFaq === id ? null : id);
  };

  return (
    <div className="min-h-screen pt-20">
      <PageTitle title="FAQ" />
      {/* Hero Section */}
      <section className="relative py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center mb-6">
              <HelpCircle className="h-12 w-12 mr-4" />
              <h1 className="text-5xl md:text-6xl font-serif">
                Frequently Asked Questions
              </h1>
            </div>
            <p className="text-xl text-gray-300 leading-relaxed">
              Find answers to common questions about our services, engagement process, and expertise
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {filteredFaqs.map((faq) => (
              <Card key={faq.id} className="border-0 shadow-lg overflow-hidden">
                <button
                  onClick={() => toggleFaq(faq.id)}
                  className="w-full text-left"
                >
                  <CardContent className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 pr-4">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            {faq.category}
                          </span>
                        </div>
                        <h3 className="text-lg font-serif text-gray-900 leading-tight">
                          {faq.question}
                        </h3>
                      </div>
                      <div className="flex-shrink-0 ml-4">
                        {openFaq === faq.id ? (
                          <ChevronUp className="h-6 w-6 text-gray-600" />
                        ) : (
                          <ChevronDown className="h-6 w-6 text-gray-600" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </button>
                
                {openFaq === faq.id && (
                  <div className="px-6 pb-6">
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-serif text-gray-900 mb-6">Still Have Questions?</h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            Our team is here to help. Contact us for personalized assistance with your legal inquiries.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 transition-colors"
          >
            Contact Us
          </a>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif mb-4">Additional Resources</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <a href="/insights" className="text-center p-6 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors">
              <h3 className="text-xl font-serif mb-2">Insights</h3>
              <p className="text-sm text-gray-300">Expert analysis and thought leadership</p>
            </a>
            <a href="/case-studies" className="text-center p-6 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors">
              <h3 className="text-xl font-serif mb-2">Case Studies</h3>
              <p className="text-sm text-gray-300">See our work in action</p>
            </a>
            <a href="/practice-areas" className="text-center p-6 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors">
              <h3 className="text-xl font-serif mb-2">Services</h3>
              <p className="text-sm text-gray-300">Explore our practice areas</p>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;